import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { IonApp, IonRouterOutlet, isPlatform } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'

import { FC, useEffect } from 'react'
import { Redirect, Route } from 'react-router-dom'

import './App.css'
import { AppToastHost } from './components/AppToastHost'
import { EmailVerificationPrompt } from './components/EmailVerificationPrompt'
import { OnboardingModal } from './components/OnboardingModal'
import Agencies from './pages/Agencies'
import AgencyDetail from './pages/AgencyDetail'
import DirectorySearch from './pages/DirectorySearch'
import EditReview from './pages/EditReview'
import GSACalc from './pages/GSACalc'
import HospitalDetail from './pages/HospitalDetail'
import Hospitals from './pages/Hospitals'
import IncomeCalc from './pages/IncomeCalc'
import Login from './pages/Login'
import Logout from './pages/Logout'
import MyPages from './pages/MyPages'
import MyReviews from './pages/MyReviews'
import Privacy from './pages/Privacy'
import Profile from './pages/Profile'
import RedirectCallback from './pages/RedirectCallback'
import Register from './pages/Register'
import Review from './pages/Review'
import Terms from './pages/Terms'
import Tools from './pages/Tools'
import { AppLoadingProvider } from './providers/AppLoadingContext'
import { AppUpdateProvider } from './providers/AppUpdateContext'
import Auth0ProviderWithHistory from './providers/Auth0ProviderWithHistory'
import { AuthStateProvider } from './providers/AuthStateContext'
import { initNativeSafeArea } from './utils/safeArea'

export const App: FC = () => {
  useEffect(() => {
    void initNativeSafeArea()

    if (Capacitor.isPluginAvailable('Keyboard') && isPlatform('iphone')) {
      Keyboard.setAccessoryBarVisible({ isVisible: true })
    }
  }, [])

  return (
    <IonApp>
      <IonReactRouter>
        <Auth0ProviderWithHistory>
          {/* OnboardingModal is placed here — outside every loading provider —
              so it is never unmounted while AppLoadingProvider or AuthStateProvider
              show their loading screens. It renders via createPortal into document.body
              so visual placement in the tree doesn't matter. */}
          <OnboardingModal />
          <EmailVerificationPrompt />
          <AppToastHost />
          <AppUpdateProvider>
            <AppLoadingProvider>
              <AuthStateProvider>
                <IonRouterOutlet>
                  <Route exact path="/redirect">
                    <RedirectCallback />
                  </Route>
                  <Route exact path="/profile">
                    <Profile />
                  </Route>
                  <Route exact path="/my-pages">
                    <MyPages />
                  </Route>
                  <Route exact path="/my-reviews">
                    <MyReviews />
                  </Route>
                  <Route exact path="/login/redirect/:redirect">
                    <Login />
                  </Route>
                  <Route exact path="/login">
                    <Login />
                  </Route>
                  <Route exact path="/register">
                    <Register />
                  </Route>
                  <Route exact path="/logout">
                    <Logout />
                  </Route>
                  <Route path="/review-hospital">
                    <Review reviewType="hospital" />
                  </Route>
                  <Route path="/review-hospital/:id">
                    <Review reviewType="hospital" />
                  </Route>
                  <Route path="/review-agency">
                    <Review reviewType="agency" />
                  </Route>
                  <Route path="/review-agency/:id">
                    <Review reviewType="agency" />
                  </Route>
                  <Route exact path="/edit-review/:id">
                    <EditReview />
                  </Route>
                  <Route exact path="/directory-search">
                    <DirectorySearch />
                  </Route>
                  <Route exact path="/hospitals">
                    <Hospitals />
                  </Route>
                  <Route exact path="/hospitals/:permalink" component={HospitalDetail} />
                  <Route exact path="/hospital">
                    <Redirect to="/hospitals" />
                  </Route>
                  <Route exact path="/agencies">
                    <Agencies />
                  </Route>
                  <Route exact path="/agencies/:permalink" component={AgencyDetail} />
                  <Route exact path="/agency">
                    <Redirect to="/agencies" />
                  </Route>
                  <Route exact path="/">
                    <Redirect to="/hospitals" />
                  </Route>
                  <Route exact path="/tools">
                    <Tools />
                  </Route>
                  <Route exact path="/income-calculator">
                    <IncomeCalc />
                  </Route>
                  <Route exact path="/gsa-calculator">
                    <GSACalc />
                  </Route>
                  <Route exact path="/privacy">
                    <Privacy />
                  </Route>
                  <Route exact path="/terms">
                    <Terms />
                  </Route>
                  {/* Default route to / */}
                  <Route>
                    <Redirect to="/" />
                  </Route>
                </IonRouterOutlet>
              </AuthStateProvider>
            </AppLoadingProvider>
          </AppUpdateProvider>
        </Auth0ProviderWithHistory>
      </IonReactRouter>
    </IonApp>
  )
}
